import Product from './Product';
import {
  announceVariant,
  captureFocus,
  restoreFocus,
} from '../../helpers/variantA11y';
import initShowcase, {
  cleanupPdpShowcaseDrawers,
} from '../../global/initShowcase';
import {
  reRunScripts,
  reinitOkendoWidgets,
  reinitSwymActionButtons,
} from '../../helpers';

const selectors = {
  swatchProductUrl: '[data-swatch-product-url]',
  product: '[data-product]',
  syncSection: '[data-product-sync]',
};

/**
 * Variant from Liquid `data-swatch-pdp-variant-id` (PDP size + this color). Optional.
 *
 * @param {HTMLElement} el
 * @returns {string | undefined}
 */
function getSwatchPdpVariantId(el) {
  const id = el.dataset.swatchPdpVariantId;
  return id && id !== '' ? id : undefined;
}

/**
 * Appends &variant= when present (Section Rendering + product request).
 *
 * @param {string} pathWithQuery
 * @param {string} [variantId]
 * @returns {string}
 */
function withVariantParam(pathWithQuery, variantId) {
  if (!variantId) {
    return pathWithQuery;
  }
  const joiner = pathWithQuery.includes('?') ? '&' : '?';
  return `${pathWithQuery}${joiner}variant=${encodeURIComponent(variantId)}`;
}

/**
 * Keeps cart-related data attrs on [data-product] in sync when only innerHTML is swapped.
 *
 * @param {HTMLElement} target
 * @param {HTMLElement} source
 */
function syncProductDataset(target, source) {
  if (source.dataset.productId) {
    target.dataset.productId = source.dataset.productId;
  }

  if (source.dataset.finalSale) {
    target.dataset.finalSale = source.dataset.finalSale;
  } else {
    delete target.dataset.finalSale;
  }

  if (source.dataset.preorder) {
    target.dataset.preorder = source.dataset.preorder;
  } else {
    delete target.dataset.preorder;
  }

  if (source.dataset.variantInventory) {
    target.dataset.variantInventory = source.dataset.variantInventory;
  }
}

/**
 * Async product switching: reads handle + optional variant from the clicked swatch (Liquid data attrs).
 *
 * @export
 * @class ProductAsyncSwitcher
 */
export default class ProductAsyncSwitcher {
  /**
   * @param {HTMLElement} el
   * @param {string} sectionId
   * @param {Object} [productConfig] - Parent Product config (e.g. isQuickShop, quickShopUrl)
   */
  constructor(el, sectionId, productConfig = {}) {
    this.el = el;
    this.sectionId = sectionId;
    this.productConfig = productConfig;

    const swatchProductUrls = el.querySelectorAll(selectors.swatchProductUrl);
    swatchProductUrls.forEach(link => {
      if (
        link.disabled ||
        link.matches('[data-product-item-swatch]') ||
        link.closest('[data-product-item]')
      ) {
        return;
      }

      const handle = link.dataset.swatchProductUrl;
      if (handle) {
        link.addEventListener('click', e => {
          e.preventDefault();
          const target = e.currentTarget;
          if (!(target instanceof HTMLElement)) {
            return;
          }
          const variantId = getSwatchPdpVariantId(target);
          if (!this.productConfig.isQuickShop) {
            this._updateURL(handle, variantId);
          }
          this._fetchProductByHandle(handle, variantId, captureFocus(this.el));
          const optionVariantQuery = variantId
            ? `&variant=${encodeURIComponent(variantId)}`
            : '';
          const syncSections = document.querySelectorAll(selectors.syncSection);
          syncSections.forEach(section => {
            const syncSectionId = section.dataset.sectionId;
            if (syncSectionId) {
              this._fetchSectionByHandle(
                section,
                handle,
                syncSectionId,
                optionVariantQuery,
              );
            }
          });
        });
      }
    });
  }

  _fetchProductByHandle(handle, variantId, focusSnapshot = null) {
    const path = `${window.Shopify.routes.root}products/${handle}`;
    const baseUrl = withVariantParam(
      `${path}?section_id=${this.sectionId}`,
      variantId,
    );
    fetch(baseUrl)
      .then(response => response.text())
      .then(responseText => {
        const html = responseText;
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const newRoot = parsedHTML.querySelector(selectors.product);
        if (!newRoot) {
          return;
        }
        syncProductDataset(this.el, newRoot);
        cleanupPdpShowcaseDrawers();
        this.el.innerHTML = newRoot.innerHTML;
        reRunScripts(this.el);
        initShowcase(this.el);
        const form = this.el.querySelector('[data-product-form]');
        const nextConfig = {
          ...this.productConfig,
          klaviyoTrackViewedProduct: true,
        };
        if (this.productConfig.isQuickShop) {
          nextConfig.quickShopUrl =
            form?.dataset.productUrl || this.productConfig.quickShopUrl;
        }
        new Product(this.el, nextConfig);
        reinitSwymActionButtons(this.el);
        // The reviews widget sits in its own section, so on the PDP it has to be
        // repointed from outside `this.el`. In quickshop it lives inside the
        // modal, and the page behind must keep its own product.
        reinitOkendoWidgets({
          scope: this.productConfig.isQuickShop ? this.el : document,
          productId: newRoot.dataset.productId,
        });
        restoreFocus(this.el, focusSnapshot);
        announceVariant(this.el);
      });
  }

  _fetchSectionByHandle(el, handle, sectionId, variantQuery = '') {
    fetch(
      `${window.Shopify.routes.root}products/${handle}?section_id=${sectionId}${variantQuery}`,
    )
      .then(response => response.text())
      .then(responseText => {
        const html = responseText;
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const newRoot = parsedHTML.querySelector(selectors.syncSection);
        el.innerHTML = newRoot.innerHTML;
        reRunScripts(el);
        reinitOkendoWidgets({ scope: el });
      });
  }

  _updateURL(handle, variantId) {
    const path = `${window.Shopify.routes.root}products/${handle}`;
    if (variantId) {
      const u = new URL(path, window.location.origin);
      u.searchParams.set('variant', variantId);
      history.replaceState({}, '', u.toString());
    } else {
      history.replaceState({}, '', path);
    }
  }
}
